import { useMemo, useState } from "react";
import {
    addDays,
    addMonths,
    addWeeks,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    isBefore,
    isSameDay,
    isSameMonth,
    isToday as isTodayFn,
    startOfDay,
    startOfMonth,
    startOfWeek,
    subDays,
    subMonths,
    subWeeks,
} from "date-fns";
import { fr } from "date-fns/locale";
import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/modal";
import { MdChevronLeft, MdChevronRight, MdClose } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Card from "components/card";
import { markTaskAsDone, rescheduleTask } from "interfaces/task";

interface Task {
    _id: string;
    content: string;
    dueDate: string;
    status: string;
    customer: {
        _id: string;
        company: string;
        assignedTo?: Array<{ _id: string; fullName: string }>;
    };
    order?: { _id: string; orderId: string } | null;
    source?: string;
    assignedTo?: { _id: string; fullName: string } | null;
}

interface TaskAgendaProps {
    tasks: Task[];
    onTaskDone: (taskId: string) => void;
    onTaskReschedule: (taskId: string, newDueDate: string) => void;
}

type ViewMode = "month" | "week" | "day";

const dayKey = (date: Date) => format(date, "yyyy-MM-dd");

const isOverdue = (date: Date | string) => {
    const d = typeof date === "string" ? new Date(date) : date;
    return isBefore(d, startOfDay(new Date())) && !isTodayFn(d);
};

const CommercialChips = ({ commercials }: { commercials?: Array<{ _id: string; fullName: string }> }) => {
    if (!commercials || commercials.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1 mt-2">
            {commercials.map((c) => (
                <span key={c._id} className="text-[11px] bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">
                    {c.fullName}
                </span>
            ))}
        </div>
    );
};

const TaskPill = ({
    task,
    onClick,
    onDragStart,
    onDragEnd,
}: {
    task: Task;
    onClick: (e: React.MouseEvent) => void;
    onDragStart: () => void;
    onDragEnd: () => void;
}) => {
    const overdue = isOverdue(task.dueDate);
    const today = isTodayFn(new Date(task.dueDate));
    return (
        <div
            draggable
            onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", task._id);
                onDragStart();
            }}
            onDragEnd={onDragEnd}
            onClick={onClick}
            title={`${task.customer?.company} — ${task.content}`}
            className={`flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] truncate cursor-grab active:cursor-grabbing ${
                overdue
                    ? "bg-red-50 text-red-600"
                    : today
                    ? "bg-toola-50 text-toola-600"
                    : "bg-lightPrimary text-navy-700"
            }`}
        >
            <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    overdue ? "bg-red-400" : today ? "bg-toola-500" : "bg-gray-400"
                }`}
            />
            <span className="truncate">{task.customer?.company}</span>
        </div>
    );
};

const TaskMiniCard = ({
    task,
    onClick,
    onDragStart,
    onDragEnd,
}: {
    task: Task;
    onClick: () => void;
    onDragStart: () => void;
    onDragEnd: () => void;
}) => {
    const overdue = isOverdue(task.dueDate);
    const today = isTodayFn(new Date(task.dueDate));
    return (
        <div
            draggable
            onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", task._id);
                onDragStart();
            }}
            onDragEnd={onDragEnd}
            onClick={onClick}
            className={`rounded-lg px-2 py-1.5 cursor-grab active:cursor-grabbing ${
                overdue ? "bg-red-50" : today ? "bg-toola-50" : "bg-lightPrimary"
            }`}
        >
            <p className="text-[11px] font-medium text-navy-700 truncate">{task.customer?.company}</p>
            <p className="text-[11px] text-gray-500 truncate">{task.content}</p>
        </div>
    );
};

const DayCard = ({
    task,
    onClick,
    onMarkDone,
}: {
    task: Task;
    onClick: () => void;
    onMarkDone: (id: string) => void;
}) => {
    const overdue = isOverdue(task.dueDate);
    const today = isTodayFn(new Date(task.dueDate));
    const commercials = task.source === "sav" && task.assignedTo ? [task.assignedTo] : task.customer?.assignedTo;
    return (
        <div
            onClick={onClick}
            className={`relative flex items-start justify-between gap-4 rounded-xl p-4 cursor-pointer ${
                overdue ? "bg-red-50" : today ? "bg-toola-50" : "bg-lightPrimary"
            }`}
        >
            <div className="flex-1 min-w-0">
                <p className="font-medium text-navy-700 text-sm truncate">{task.customer?.company}</p>
                <p className="text-gray-500 text-sm mt-0.5">{task.content}</p>
                <CommercialChips commercials={commercials} />
            </div>
            <button
                className="flex-shrink-0 bg-toola-400 text-white text-xs py-1.5 px-3 rounded-lg whitespace-nowrap"
                onClick={async (e) => {
                    e.stopPropagation();
                    await onMarkDone(task._id);
                }}
            >
                Tâche effectuée
            </button>
        </div>
    );
};

const MonthView = ({
    cursor,
    tasksByDay,
    onTaskClick,
    onDayClick,
    dragOverKey,
    setDraggedId,
    setDragOverKey,
    onDrop,
}: {
    cursor: Date;
    tasksByDay: Record<string, Task[]>;
    onTaskClick: (task: Task) => void;
    onDayClick: (date: Date) => void;
    dragOverKey: string | null;
    setDraggedId: (id: string | null) => void;
    setDragOverKey: (key: string | null) => void;
    onDrop: (date: Date) => void;
}) => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });
    const weekDayLabels = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

    return (
        <div className="border border-gray-100 rounded-xl overflow-hidden">
            <div className="grid grid-cols-7 bg-lightPrimary">
                {weekDayLabels.map((l) => (
                    <div key={l} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase">
                        {l}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7">
                {days.map((date) => {
                    const key = dayKey(date);
                    const dayTasks = tasksByDay[key] || [];
                    const inMonth = isSameMonth(date, cursor);
                    const today = isTodayFn(date);
                    const isOver = dragOverKey === key;
                    return (
                        <div
                            key={key}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOverKey(key);
                            }}
                            onDragLeave={() => setDragOverKey(null)}
                            onDrop={(e) => {
                                e.preventDefault();
                                onDrop(date);
                            }}
                            onClick={() => onDayClick(date)}
                            className={`min-h-[110px] border-t border-l border-gray-100 p-1.5 cursor-pointer transition ${
                                inMonth ? "bg-white" : "bg-gray-50/60"
                            } ${isOver ? "ring-2 ring-inset ring-toola-400 bg-toola-50" : ""}`}
                        >
                            <div className="flex justify-end">
                                <span
                                    className={`text-xs w-6 h-6 flex items-center justify-center rounded-full ${
                                        today
                                            ? "bg-toola-500 text-white font-semibold"
                                            : inMonth
                                            ? "text-navy-700"
                                            : "text-gray-300"
                                    }`}
                                >
                                    {format(date, "d")}
                                </span>
                            </div>
                            <div className="space-y-1 mt-1">
                                {dayTasks.slice(0, 3).map((task) => (
                                    <TaskPill
                                        key={task._id}
                                        task={task}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onTaskClick(task);
                                        }}
                                        onDragStart={() => setDraggedId(task._id)}
                                        onDragEnd={() => {
                                            setDraggedId(null);
                                            setDragOverKey(null);
                                        }}
                                    />
                                ))}
                                {dayTasks.length > 3 && (
                                    <p className="text-[10px] text-gray-400 pl-1">
                                        +{dayTasks.length - 3} de plus
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const WeekView = ({
    cursor,
    tasksByDay,
    onTaskClick,
    onDayClick,
    dragOverKey,
    setDraggedId,
    setDragOverKey,
    onDrop,
}: {
    cursor: Date;
    tasksByDay: Record<string, Task[]>;
    onTaskClick: (task: Task) => void;
    onDayClick: (date: Date) => void;
    dragOverKey: string | null;
    setDraggedId: (id: string | null) => void;
    setDragOverKey: (key: string | null) => void;
    onDrop: (date: Date) => void;
}) => {
    const start = startOfWeek(cursor, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end: addDays(start, 6) });

    return (
        <div className="grid grid-cols-1 sm:grid-cols-7 gap-3">
            {days.map((date) => {
                const key = dayKey(date);
                const dayTasks = tasksByDay[key] || [];
                const today = isTodayFn(date);
                const isOver = dragOverKey === key;
                return (
                    <div
                        key={key}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragOverKey(key);
                        }}
                        onDragLeave={() => setDragOverKey(null)}
                        onDrop={(e) => {
                            e.preventDefault();
                            onDrop(date);
                        }}
                        className={`rounded-xl border p-2 min-h-[220px] flex flex-col transition ${
                            today ? "border-toola-300 bg-toola-50/30" : "border-gray-100"
                        } ${isOver ? "ring-2 ring-inset ring-toola-400 bg-toola-50" : ""}`}
                    >
                        <button onClick={() => onDayClick(date)} className="flex items-center justify-between mb-2">
                            <span
                                className={`text-xs font-semibold uppercase ${
                                    today ? "text-toola-600" : "text-gray-500"
                                }`}
                            >
                                {format(date, "EEE", { locale: fr })}
                            </span>
                            <span
                                className={`text-sm w-6 h-6 flex items-center justify-center rounded-full ${
                                    today ? "bg-toola-500 text-white font-semibold" : "text-navy-700"
                                }`}
                            >
                                {format(date, "d")}
                            </span>
                        </button>
                        <div className="space-y-1.5 overflow-y-auto flex-1">
                            {dayTasks.map((task) => (
                                <TaskMiniCard
                                    key={task._id}
                                    task={task}
                                    onClick={() => onTaskClick(task)}
                                    onDragStart={() => setDraggedId(task._id)}
                                    onDragEnd={() => {
                                        setDraggedId(null);
                                        setDragOverKey(null);
                                    }}
                                />
                            ))}
                            {dayTasks.length === 0 && (
                                <p className="text-[11px] text-gray-300 text-center mt-4">—</p>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const DayView = ({
    cursor,
    tasksByDay,
    onTaskClick,
    onMarkDone,
}: {
    cursor: Date;
    tasksByDay: Record<string, Task[]>;
    onTaskClick: (task: Task) => void;
    onMarkDone: (id: string) => void;
}) => {
    const dayTasks = tasksByDay[dayKey(cursor)] || [];

    if (dayTasks.length === 0) {
        return (
            <div className="text-center py-[70px]">
                <p className="text-xl text-gray-400 font-medium">Aucune tâche prévue ce jour-là.</p>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {dayTasks.map((task) => (
                <DayCard key={task._id} task={task} onClick={() => onTaskClick(task)} onMarkDone={onMarkDone} />
            ))}
        </div>
    );
};

const TaskAgenda = ({ tasks, onTaskDone, onTaskReschedule }: TaskAgendaProps) => {
    const [view, setView] = useState<ViewMode>("week");
    const [cursor, setCursor] = useState<Date>(new Date());
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [draggedId, setDraggedId] = useState<string | null>(null);
    const [dragOverKey, setDragOverKey] = useState<string | null>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();

    const tasksByDay = useMemo(() => {
        const map: Record<string, Task[]> = {};
        tasks.forEach((t) => {
            const key = dayKey(new Date(t.dueDate));
            if (!map[key]) map[key] = [];
            map[key].push(t);
        });
        Object.values(map).forEach((list) =>
            list.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
        );
        return map;
    }, [tasks]);

    const openTask = (task: Task) => {
        setSelectedTask(task);
        onOpen();
    };

    const handleMarkDone = async (taskId: string) => {
        await markTaskAsDone(taskId);
        onTaskDone(taskId);
        onClose();
    };

    const applyReschedule = async (task: Task, newDate: Date) => {
        const original = new Date(task.dueDate);
        if (isSameDay(original, newDate)) return;
        newDate.setHours(original.getHours(), original.getMinutes(), original.getSeconds(), 0);
        const iso = newDate.toISOString();
        onTaskReschedule(task._id, iso);
        await rescheduleTask(task._id, iso);
    };

    const handleDrop = async (targetDate: Date) => {
        setDragOverKey(null);
        const taskId = draggedId;
        setDraggedId(null);
        const task = tasks.find((t) => t._id === taskId);
        if (!task) return;
        await applyReschedule(task, new Date(targetDate));
    };

    const goPrev = () =>
        setCursor((prev) =>
            view === "month" ? subMonths(prev, 1) : view === "week" ? subWeeks(prev, 1) : subDays(prev, 1)
        );
    const goNext = () =>
        setCursor((prev) =>
            view === "month" ? addMonths(prev, 1) : view === "week" ? addWeeks(prev, 1) : addDays(prev, 1)
        );
    const goToday = () => setCursor(new Date());

    const title = useMemo(() => {
        if (view === "month") return format(cursor, "MMMM yyyy", { locale: fr });
        if (view === "day") return format(cursor, "EEEE d MMMM yyyy", { locale: fr });
        const start = startOfWeek(cursor, { weekStartsOn: 1 });
        const end = endOfWeek(cursor, { weekStartsOn: 1 });
        return isSameMonth(start, end)
            ? `${format(start, "d", { locale: fr })} – ${format(end, "d MMMM yyyy", { locale: fr })}`
            : `${format(start, "d MMM", { locale: fr })} – ${format(end, "d MMM yyyy", { locale: fr })}`;
    }, [view, cursor]);

    const goToDay = (date: Date) => {
        setCursor(date);
        setView("day");
    };

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-1">
                    <button
                        onClick={goPrev}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-lightPrimary"
                    >
                        <MdChevronLeft size={20} />
                    </button>
                    <button
                        onClick={goToday}
                        className="text-xs font-medium text-gray-500 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-lightPrimary"
                    >
                        Aujourd'hui
                    </button>
                    <button
                        onClick={goNext}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-lightPrimary"
                    >
                        <MdChevronRight size={20} />
                    </button>
                    <h5 className="ml-2 text-lg font-semibold text-navy-700 capitalize">{title}</h5>
                </div>
                <div className="flex items-center gap-1 bg-lightPrimary rounded-xl p-1">
                    {(["day", "week", "month"] as ViewMode[]).map((v) => (
                        <button
                            key={v}
                            onClick={() => setView(v)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                                view === v ? "bg-white text-toola-500 shadow-sm" : "text-gray-500"
                            }`}
                        >
                            {v === "day" ? "Jour" : v === "week" ? "Semaine" : "Mois"}
                        </button>
                    ))}
                </div>
            </div>

            {view === "month" && (
                <MonthView
                    cursor={cursor}
                    tasksByDay={tasksByDay}
                    onTaskClick={openTask}
                    onDayClick={goToDay}
                    dragOverKey={dragOverKey}
                    setDraggedId={setDraggedId}
                    setDragOverKey={setDragOverKey}
                    onDrop={handleDrop}
                />
            )}
            {view === "week" && (
                <WeekView
                    cursor={cursor}
                    tasksByDay={tasksByDay}
                    onTaskClick={openTask}
                    onDayClick={goToDay}
                    dragOverKey={dragOverKey}
                    setDraggedId={setDraggedId}
                    setDragOverKey={setDragOverKey}
                    onDrop={handleDrop}
                />
            )}
            {view === "day" && (
                <DayView cursor={cursor} tasksByDay={tasksByDay} onTaskClick={openTask} onMarkDone={handleMarkDone} />
            )}

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay className="bg-[#000] !opacity-30" />
                <ModalContent className="md:top-[12vh] !m-auto !w-max !max-w-[92%] !z-[1002] !overflow-hidden">
                    <ModalBody className="p-0">
                        <Card extra="p-6 w-[440px] max-w-full max-h-[80vh] overflow-y-auto">
                            {selectedTask && (
                                <>
                                    <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100">
                                        <div>
                                            <h2 className="text-lg font-semibold text-navy-700">
                                                {selectedTask.customer?.company}
                                            </h2>
                                            <p className="text-xs text-gray-400 mt-1 capitalize">
                                                {format(new Date(selectedTask.dueDate), "EEEE d MMMM yyyy", {
                                                    locale: fr,
                                                })}
                                            </p>
                                        </div>
                                        <button
                                            onClick={onClose}
                                            className="text-gray-400 hover:text-gray-600 mt-0.5"
                                        >
                                            <MdClose className="w-5 h-5" />
                                        </button>
                                    </div>

                                    <div className="space-y-3">
                                        <p className="text-sm text-gray-600">{selectedTask.content}</p>
                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                            <label htmlFor="reschedule-date">Replanifier au</label>
                                            <input
                                                id="reschedule-date"
                                                type="date"
                                                defaultValue={format(
                                                    new Date(selectedTask.dueDate),
                                                    "yyyy-MM-dd"
                                                )}
                                                onChange={async (e) => {
                                                    if (!e.target.value || !selectedTask) return;
                                                    await applyReschedule(selectedTask, new Date(e.target.value));
                                                    onClose();
                                                }}
                                                className="border border-gray-200 rounded-lg text-sm py-1 px-2 text-navy-700"
                                            />
                                        </div>
                                        <CommercialChips
                                            commercials={
                                                selectedTask.source === "sav" && selectedTask.assignedTo
                                                    ? [selectedTask.assignedTo]
                                                    : selectedTask.customer?.assignedTo
                                            }
                                        />
                                        <div className="flex flex-col gap-1">
                                            {selectedTask.customer && (
                                                <button
                                                    onClick={() => navigate(`/customer/${selectedTask.customer._id}`)}
                                                    className="text-sm text-toola-500 hover:underline text-left"
                                                >
                                                    Voir la fiche client →
                                                </button>
                                            )}
                                            {selectedTask.order && (
                                                <button
                                                    onClick={() => navigate(`/order/${selectedTask.order._id}`)}
                                                    className="text-sm text-toola-500 hover:underline text-left"
                                                >
                                                    Voir la commande →
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-6 flex gap-3 pt-4 border-t border-gray-100">
                                        <button
                                            onClick={onClose}
                                            className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                                        >
                                            Fermer
                                        </button>
                                        <button
                                            onClick={() => handleMarkDone(selectedTask._id)}
                                            className="linear flex-1 rounded-xl bg-toola-500 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-toola-600"
                                        >
                                            Tâche effectuée
                                        </button>
                                    </div>
                                </>
                            )}
                        </Card>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </div>
    );
};

export default TaskAgenda;
